import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Star, Lock, Loader2, Play, Check, Youtube } from "lucide-react";
import api from "@/lib/api";
import { subjectIcon, BILINGUAL_SUBJECTS, subjectLabel, levelLabel } from "@/lib/subjects";
import FloatingCalculator from "@/components/Calculator";
import LessonVideos from "@/components/LessonVideos";
import TestPaperList from "@/components/TestPaperList";
import { Button } from "@/components/ui/button";

const LEVEL_COLORS = ["#10B981", "#F59E0B", "#EF4444"];

export default function SubjectView() {
  const { childId, subject } = useParams();
  const decodedSubject = decodeURIComponent(subject);
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = () => {
    api.get(`/children/${childId}/subject/${encodeURIComponent(decodedSubject)}`)
      .then(({ data }) => setData(data))
      .catch(() => navigate(`/kid/${childId}`));
  };

  useEffect(load, [childId, decodedSubject]); // eslint-disable-line

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#EAF7F4]"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;
  }

  const Icon = subjectIcon(decodedSubject);
  const frTitles = BILINGUAL_SUBJECTS.includes(decodedSubject) && localStorage.getItem("aa_voice_lang") === "fr";
  const chTitle = (c) => (frTitles && c.title_fr) || c.title;
  const subjColor = { Maths: "#FF6B4A", English: "#3D8BFF", French: "#FFB020", "History & Geography": "#22C08A", Science: "#A66CFF", SSEE: "#14B8A6", "Health & Physical Education": "#EC4899" }[decodedSubject] || "#3B82F6";

  const startLevel = (chapter, level) => {
    const q = new URLSearchParams({
      subject: decodedSubject,
      chapter: chapter.chapter_id,
      title: chapter.title,
      titleFr: chapter.title_fr || chapter.title,
      level: String(level.level),
      levelName: level.name,
    });
    navigate(`/kid/${childId}/play?${q.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[#EAF7F4] font-kids text-slate-900">
      <header className="max-w-3xl mx-auto px-5 pt-6">
        <Button variant="ghost" onClick={() => navigate(`/kid/${childId}`)} className="rounded-full font-parent" data-testid="subject-back">
          <ArrowLeft size={18} className="mr-1" /> Back
        </Button>
      </header>

      <div className="max-w-3xl mx-auto px-5 pt-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl border-4 border-slate-900 flex items-center justify-center" style={{ background: subjColor, boxShadow: "4px 4px 0 0 #0F172A" }}>
            <Icon color="#fff" size={30} />
          </div>
          <div>
            <h1 className="text-3xl font-bold" data-testid="subject-title">{frTitles ? subjectLabel(decodedSubject, "fr") : decodedSubject}</h1>
            <p className="font-parent text-slate-500">{data.paid ? (frTitles ? "Tous les chapitres débloqués" : "All chapters unlocked") : (frTitles ? "Verrouillé — demande à tes parents de choisir un forfait" : "Locked — ask your parent to choose a plan")}</p>
          </div>
        </div>

        <TestPaperList childId={childId} subject={decodedSubject} fr={decodedSubject === "French" || frTitles} />

        <div className="mt-8 space-y-8 pb-16">
          {data.chapters.map((chapter, ci) => {
            return (
            <motion.div key={chapter.chapter_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: ci * 0.08 }}
              className="nb-card bg-white p-6" data-testid={`chapter-${ci}`}>
              <div className="flex items-center gap-3 mb-5 flex-wrap">
                <span className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm shrink-0">{ci + 1}</span>
                <h2 className="text-xl font-bold flex-1" data-testid={`chapter-title-${ci}`}>{chTitle(chapter)}</h2>
                {chapter.video_url && (
                  <a href={chapter.video_url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-parent font-bold text-red-600 bg-red-50 border-2 border-slate-900 rounded-full px-3 py-1.5 hover:translate-x-0.5 hover:translate-y-0.5 transition-transform"
                    style={{ boxShadow: "2px 2px 0 0 #0F172A" }}
                    data-testid={`watch-video-${ci}`}>
                    <Youtube size={14} /> Watch
                  </a>
                )}
              </div>
              <LessonVideos lessons={chapter.lessons} paid={data.paid} fr={decodedSubject === "French" || frTitles} ci={ci}
                start={data.chapters.slice(0, ci).reduce((t, c) => t + (c.lessons?.length || 0), 0)} />
              <div className="grid grid-cols-3 gap-3">
                {chapter.levels.map((lv) => {
                  const color = LEVEL_COLORS[lv.level - 1];
                  return (
                    <button
                      key={lv.level}
                      disabled={!lv.unlocked}
                      onClick={() => startLevel(chapter, lv)}
                      data-testid={`level-${ci}-${lv.level}`}
                      className={`relative rounded-2xl border-4 border-slate-900 p-4 flex flex-col items-center gap-2 transition-all ${
                        lv.unlocked
                          ? "bg-white hover:translate-x-0.5 hover:translate-y-0.5 cursor-pointer"
                          : "bg-slate-100 opacity-70 cursor-not-allowed"
                      }`}
                      style={lv.unlocked ? { boxShadow: "4px 4px 0 0 #0F172A" } : {}}
                    >
                      <div className="w-11 h-11 rounded-full flex items-center justify-center border-2 border-slate-900" style={{ background: lv.unlocked ? color : "#CBD5E1" }}>
                        {lv.completed ? <Check color="#fff" size={22} strokeWidth={3} /> : lv.unlocked ? <Play color="#fff" size={20} fill="#fff" /> : <Lock color="#fff" size={18} />}
                      </div>
                      <span className="text-xs font-bold text-center leading-tight" data-testid={`level-name-${ci}-${lv.level}`}>{frTitles ? levelLabel(lv.name, "fr") : lv.name}</span>
                      <div className="flex gap-0.5 h-4">
                        {lv.completed && [1, 2, 3].map((s) => (
                          <Star key={s} size={13} className={s <= lv.stars ? "text-yellow-400" : "text-slate-200"} fill={s <= lv.stars ? "#FACC15" : "#E2E8F0"} />
                        ))}
                      </div>
                      {lv.is_demo && (
                        <span className="absolute -top-2 -right-2 bg-yellow-300 border-2 border-slate-900 text-[9px] font-parent font-bold px-1.5 py-0.5 rounded-full">FREE</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
            );
          })}
        </div>
      </div>
      <FloatingCalculator />
    </div>
  );
}
